import { useEffect, useMemo, useRef, useState } from "react";
import { Download, FileVideo, Play, RefreshCw } from "lucide-react";
import { MotionCanvas, type MotionCanvasHandle } from "./components/MotionCanvas";
import { loadLatestData, loadWeeklySummary } from "./lib/data/loaders";
import { SYMBOLS } from "./lib/market-data/symbols";
import type { MarketSnapshot, WeeklySummary } from "./lib/market-data/types";
import { validateDailyBar } from "./lib/market-data/validation";
import { getMp4Capability } from "./lib/recording/mp4";
import { downloadBlob, recordCanvas } from "./lib/recording/recorder";
import { safeFileName } from "./lib/recording/filenames";
import { getSkin, SKINS } from "./lib/skins/registry";
import type { SkinId } from "./lib/skins/types";
import { ACTUAL_SP500, CRASH_SP500, createManualSnapshot, type ScenarioId, SURGE_SP500 } from "./lib/scenarios/demo";
import { timeline, weeklyTimeline } from "./lib/motion/timeline";
import { DEFAULT_RENDER_SETTINGS, GLOW_INTENSITIES, type GlowIntensity } from "./lib/motion/render-settings";

const scenarioSnapshots: Record<Exclude<ScenarioId, "manual">, MarketSnapshot> = {
  actual: ACTUAL_SP500,
  surge: SURGE_SP500,
  crash: CRASH_SP500
};

type ManualInput = {
  previousClose: string;
  open: string;
  high: string;
  low: string;
  close: string;
  date: string;
};

const defaultManual: ManualInput = {
  previousClose: "7420.10",
  open: "7487.36",
  high: "7511.07",
  low: "7468.32",
  close: "7500.58",
  date: "2026-06-18"
};

const RECORDING_FPS = 30;
const RECORDING_BITRATE = 8_000_000;
const DAILY_HOLD_MS = 220;

function initialMode(): "daily" | "weekly" {
  if (typeof window === "undefined") return "daily";
  return new URLSearchParams(window.location.search).get("mode") === "weekly" ? "weekly" : "daily";
}

function initialSkinId(): SkinId {
  if (typeof window === "undefined") return "cool";
  const skin = new URLSearchParams(window.location.search).get("skin");
  return SKINS.some((item) => item.id === skin) ? (skin as SkinId) : "cool";
}

function initialScenarioId(): ScenarioId {
  if (typeof window === "undefined") return "actual";
  const scenario = new URLSearchParams(window.location.search).get("scenario");
  return scenario === "surge" || scenario === "crash" || scenario === "manual" ? scenario : "actual";
}

function initialStaticPreview(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("preview") === "final";
}

function statusLabel(status: MarketSnapshot["status"]): string {
  if (status === "available") return "利用可能";
  if (status === "stale") return "更新待ち";
  if (status === "unavailable") return "取得不可";
  return "準備中";
}

function pathLabel(pathSource: MarketSnapshot["pathSource"]): string {
  if (pathSource === "synthetic_from_ohlc") return "日足から描画";
  if (pathSource === "real_intraday") return "実時間足";
  if (pathSource === "manual") return "手動入力";
  return "デモ";
}

export function App() {
  const canvasRef = useRef<MotionCanvasHandle | null>(null);
  const [status, setStatus] = useState(() => initialMode() === "weekly" && initialStaticPreview() ? "週間プレビュー完了" : "準備完了");
  const [mode, setMode] = useState<"daily" | "weekly">(() => initialMode());
  const [symbolId, setSymbolId] = useState("sp500");
  const [scenarioId, setScenarioId] = useState<ScenarioId>(() => initialScenarioId());
  const [skinId, setSkinId] = useState<SkinId>(() => initialSkinId());
  const [glowIntensity, setGlowIntensity] = useState<GlowIntensity>(DEFAULT_RENDER_SETTINGS.glowIntensity);
  const [latest, setLatest] = useState<MarketSnapshot[]>([ACTUAL_SP500]);
  const [weekly, setWeekly] = useState<WeeklySummary | null>(null);
  const [manualInput, setManualInput] = useState<ManualInput>(defaultManual);
  const [busy, setBusy] = useState(false);
  const mp4 = getMp4Capability();
  const staticPreview = initialStaticPreview();

  useEffect(() => {
    void loadLatestData().then((data) => {
      if (data?.symbols.length) setLatest(data.symbols);
    });
  }, []);

  useEffect(() => {
    void loadWeeklySummary(symbolId).then(setWeekly);
  }, [symbolId]);

  useEffect(() => {
    if (mode === "weekly" && weekly) {
      setStatus(staticPreview ? "週間プレビュー完了" : "週間データ準備完了");
    }
  }, [mode, staticPreview, weekly]);

  const skin = getSkin(skinId);
  const renderSettings = useMemo(() => ({ glowIntensity }), [glowIntensity]);
  const snapshot = useMemo(() => {
    const symbol = SYMBOLS.find((item) => item.id === symbolId);
    if (scenarioId === "manual") {
      return createManualSnapshot({
        symbolId,
        previousClose: Number(manualInput.previousClose),
        open: Number(manualInput.open),
        high: Number(manualInput.high),
        low: Number(manualInput.low),
        close: Number(manualInput.close),
        date: manualInput.date
      });
    }
    if (scenarioId === "actual") {
      const found = latest.find((item) => item.symbolId === symbolId);
      if (found) return found;
      return {
        ...ACTUAL_SP500,
        symbolId,
        label: symbol?.label ?? ACTUAL_SP500.label,
        shortLabel: symbol?.shortLabel ?? ACTUAL_SP500.shortLabel,
        market: symbol?.market ?? ACTUAL_SP500.market,
        currency: symbol?.currency ?? ACTUAL_SP500.currency,
        timezone: symbol?.timezone ?? ACTUAL_SP500.timezone,
        decimals: symbol?.decimals ?? ACTUAL_SP500.decimals,
        flagOrIcon: symbol?.flagOrIcon ?? ACTUAL_SP500.flagOrIcon,
        status: "not_ready" as const,
        source: "fixture-placeholder"
      };
    }
    return {
      ...scenarioSnapshots[scenarioId],
      symbolId,
      label: symbol?.label ?? scenarioSnapshots[scenarioId].label,
      shortLabel: symbol?.shortLabel ?? scenarioSnapshots[scenarioId].shortLabel,
      market: symbol?.market ?? scenarioSnapshots[scenarioId].market,
      currency: symbol?.currency ?? scenarioSnapshots[scenarioId].currency,
      timezone: symbol?.timezone ?? scenarioSnapshots[scenarioId].timezone,
      decimals: symbol?.decimals ?? scenarioSnapshots[scenarioId].decimals,
      flagOrIcon: symbol?.flagOrIcon ?? scenarioSnapshots[scenarioId].flagOrIcon,
      pathSource: "demo" as const
    };
  }, [scenarioId, symbolId, latest, manualInput]);

  const manualErrors = scenarioId === "manual" ? validateDailyBar(snapshot) : [];

  async function exportVideo(format: "webm" | "mp4") {
    const canvas = canvasRef.current?.getCanvas();
    if (!canvas || busy || manualErrors.length > 0) return;
    setBusy(true);
    const durationMs = mode === "weekly" ? weeklyTimeline.totalDurationMs : timeline.totalDurationMs + DAILY_HOLD_MS;
    try {
      const blob = await recordCanvas({
        canvas,
        durationMs,
        format,
        fps: RECORDING_FPS,
        videoBitsPerSecond: RECORDING_BITRATE,
        onStatus: (nextStatus) => setStatus(`${nextStatus} ${format.toUpperCase()} ${RECORDING_FPS}fps`),
        start: () => canvasRef.current?.replay()
      });
      downloadBlob(blob, safeFileName([snapshot.symbolId, snapshot.date, mode, skin.id], format));
      setStatus(`${format.toUpperCase()}を書き出しました ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "書き出しに失敗しました");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="app-shell">
      <section className="workspace" aria-label="Index Motion Studio">
        <div className="stage">
          <MotionCanvas
            ref={canvasRef}
            snapshot={snapshot}
            weekly={weekly}
            skin={skin}
            scenarioId={scenarioId}
            mode={mode}
            settings={renderSettings}
            staticPreview={staticPreview}
            onStatus={setStatus}
          />
        </div>
        <aside className="control-panel">
          <div className="brand">
            <span>Index Motion Studio</span>
            <strong>{status}</strong>
          </div>

          <div className="segmented" aria-label="テンプレート">
            <button className={mode === "daily" ? "active" : ""} onClick={() => setMode("daily")}>単日</button>
            <button className={mode === "weekly" ? "active" : ""} onClick={() => setMode("weekly")}>週間</button>
          </div>

          <label>
            銘柄
            <select value={symbolId} onChange={(event) => setSymbolId(event.target.value)}>
              {SYMBOLS.map((symbol) => (
                <option key={symbol.id} value={symbol.id}>{symbol.label}</option>
              ))}
            </select>
          </label>

          <label>
            シナリオ
            <select value={scenarioId} onChange={(event) => setScenarioId(event.target.value as ScenarioId)}>
              <option value="actual">通常</option>
              <option value="surge">急騰デモ</option>
              <option value="crash">急落デモ</option>
              <option value="manual">手動入力</option>
            </select>
          </label>

          <label>
            スキン
            <select value={skinId} onChange={(event) => setSkinId(event.target.value as SkinId)}>
              {SKINS.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </label>

          <label>
            発光
            <select value={glowIntensity} onChange={(event) => setGlowIntensity(event.target.value as GlowIntensity)}>
              {Object.entries(GLOW_INTENSITIES).map(([value, item]) => (
                <option key={value} value={value}>{item.label}</option>
              ))}
            </select>
          </label>

          {scenarioId === "manual" && (
            <div className="manual-grid">
              {(["date", "previousClose", "open", "high", "low", "close"] as const).map((key) => (
                <label key={key}>
                  {key}
                  <input
                    type={key === "date" ? "date" : "number"}
                    step="0.01"
                    value={manualInput[key]}
                    onChange={(event) => setManualInput((current) => ({ ...current, [key]: event.target.value }))}
                  />
                </label>
              ))}
            </div>
          )}

          {manualErrors.length > 0 && <p className="error">{manualErrors[0]}</p>}

          <div className="actions">
            <button onClick={() => canvasRef.current?.replay()} disabled={busy}>
              <Play size={18} /> 再生
            </button>
            <button onClick={() => void exportVideo("webm")} disabled={busy || manualErrors.length > 0}>
              <Download size={18} /> WebM
            </button>
            <button onClick={() => void exportVideo("mp4")} disabled={busy || !mp4.direct || manualErrors.length > 0} title={mp4.fallbackMessage}>
              <FileVideo size={18} /> MP4
            </button>
            <button onClick={() => window.location.reload()} disabled={busy}>
              <RefreshCw size={18} /> 再読み込み
            </button>
          </div>

          <dl className="metadata">
            <div><dt>更新状態</dt><dd>{statusLabel(snapshot.status)}</dd></div>
            <div><dt>基準日</dt><dd>{snapshot.dataAsOf}</dd></div>
            <div><dt>値動き種別</dt><dd>{pathLabel(snapshot.pathSource)}</dd></div>
            <div><dt>MP4</dt><dd>{mp4.direct ? "直接録画可" : "WebM推奨"}</dd></div>
            <div><dt>動画</dt><dd>{RECORDING_FPS}fps / {(RECORDING_BITRATE / 1_000_000).toFixed(0)}Mbps</dd></div>
          </dl>
        </aside>
      </section>
    </main>
  );
}
